import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader } from 'components/Shared/Loader'
import { EditUserAccountForm } from 'components/Users/EditUserForm/EditUserAccountForm'
import { UserForm } from 'components/Users/UserForm'
import { UserDetailsSchema } from 'components/Users/validationSchema'
import { editUser, fetchUser } from 'services'
import { SuccessDialog } from 'components/Shared/Dialogs/SuccessDialog'
import internalStyles from 'components/Users/EditUserForm/EditUserForm.module.css'
import { triggerFormSubmit } from 'utils/utils'
import { TitleBar } from 'components/Shared/CrudComponents/TitleBar'
import styles from 'components/Shared/CrudComponents/SimpleForm.module.css'
import { useErrorHandling } from 'context/errorHandling'
import { HTTP_404_ERROR } from 'Constants'
import { useNavigatePrevent } from 'context/navigatePrevent'
import { FormikForm } from 'components/Shared/FormikForm'
import Module from 'constants/customFieldModules'
import { useAuth, USER_ACCOUNT_WRITE } from 'context/auth'
import { addGroupMember, deleteGroupMember, fetchUserGroupMemberships } from 'services/GroupsService'
import { EXTRA, MAIN } from 'components/Groups/GroupsForm/MembershipType'

export function EditUserForm (props) {
  const [editedDialogOpen, setEditedDialogOpen] = useState(false)
  const [user, setUser] = useState(null)
  const [userGroupMemberships, setUserGroupMemberships] = useState(null)

  const { t } = useTranslation()
  const { handleError } = useErrorHandling()
  const { hasAnyPrivileges } = useAuth()
  const { setNavigationUnlocked } = useNavigatePrevent()

  const editUserFormRef = useRef()

  const userId = props.match.params.userId
  useEffect(() => {
      fetchUser(userId)
        .then(setUser)
        .catch(error =>
          handleError(error, {
            [HTTP_404_ERROR]: 'notifications.error.userNotFound'
          }).then(() => {
            setNavigationUnlocked(history => history.push('/users'))
          })
        )
    }, [userId, handleError, setNavigationUnlocked]
  )

  const initialMainGroupId = userGroupMemberships
    ?.find(membership => membership.membershipType === MAIN)?.groupId
  const initialExtraGroupsIds = userGroupMemberships
    ?.filter(membership => membership.membershipType === EXTRA)
    ?.map(membership => membership.groupId)

  const removeMainGroup = useCallback((mainGroupId) => {
    if (!initialMainGroupId || initialMainGroupId === mainGroupId) return Promise.resolve()

    return deleteGroupMember(initialMainGroupId, userId)
  }, [initialMainGroupId, userId])

  const addMainGroup = useCallback((mainGroupId) => {
    if (!mainGroupId || initialMainGroupId === mainGroupId) return Promise.resolve()

    return addGroupMember(mainGroupId, {
      userId,
      membershipType: MAIN
    })
  }, [initialMainGroupId, userId])

  const removeExtraGroups = useCallback((extraGroupsIds) => {
    const groupIdsToRemove = initialExtraGroupsIds.filter(id => !extraGroupsIds.includes(id))
    return Promise.all(
      groupIdsToRemove.map(groupId => deleteGroupMember(groupId, userId))
    )
  }, [initialExtraGroupsIds, userId])

  const addExtraGroups = useCallback((extraGroupsIds) => {
    const groupIdsToAdd = extraGroupsIds.filter(id => !initialExtraGroupsIds.includes(id))
    return Promise.all(
      groupIdsToAdd.map(groupId => addGroupMember(groupId, {
        userId,
        membershipType: EXTRA
      })))
  }, [initialExtraGroupsIds, userId])

  const onUserEdit = useCallback(({ mainGroupId, extraGroupsIds, ...values }, onSuccess) =>
    editUser({
      id: userId,
      ...values
    }).then(() => onSuccess(userId)
      .then(() => Promise.all([
          removeMainGroup(mainGroupId),
          removeExtraGroups(extraGroupsIds)
        ])
          .then(() => Promise.all([
            addMainGroup(mainGroupId),
            addExtraGroups(extraGroupsIds)
          ]))
          .then(() => setEditedDialogOpen(true))
          .catch(handleError)
      )
      .catch(handleError)
    )
      .catch(error =>
        handleError(error, {
          [HTTP_404_ERROR]: 'notifications.error.userNotFound'
        })
      ), [addMainGroup, addExtraGroups, removeMainGroup, removeExtraGroups, userId, handleError])

  useEffect(() => {
    fetchUserGroupMemberships(userId)
      .then(setUserGroupMemberships)
      .catch(handleError)
  }, [handleError, userId])

  return <div>
    <TitleBar
      buttonLabel={t('user.form.submit')}
      onButtonClick={triggerFormSubmit(editUserFormRef)}
      title={t('user.form.editTitle')}/>
    <SuccessDialog open={editedDialogOpen}
                   onClose={() => {
                     setEditedDialogOpen(false)
                     setNavigationUnlocked(history => history.push('/users'))
                   }} content={t('notifications.success.editedUserDetails')}/>
    {user && userGroupMemberships ? <div className={styles.containerRow}>
        <FormikForm
          innerRef={editUserFormRef}
          enableReinitialize
          initialValues={{
            role: user.role,
            firstName: user.firstName,
            lastName: user.lastName,
            phoneNumber: user.phoneNumber,
            email: user.email,
            personalPhoneNumber: user.personalPhoneNumber,
            hash: user.hash,
            externalId: user.externalId,
            mainGroupId: initialMainGroupId,
            extraGroupsIds: initialExtraGroupsIds
          }}
          validationSchema={UserDetailsSchema}
          onSubmit={onUserEdit}
          customFields={{
            module: Module.Users,
            entityId: user?.id
          }}>
          <UserForm/>
        </FormikForm>
        <EditUserAccountForm
          className={internalStyles.editUserCredentials}
          user={user}
          disabled={!hasAnyPrivileges([USER_ACCOUNT_WRITE])}
          inEditView/>
      </div>
      :
      <Loader/>
    }
  </div>
}
